/**
 * NavigationButton Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 471-148
 *
 * A navigation button component with icon and text support.
 * Supports light/dark modes and interactive states.
 */

import React, { useState } from 'react';
import { GestureResponderEvent, Pressable, StyleSheet, Text, View } from 'react-native';
import { Icon, type IconName } from '../icons';
import { colors } from '../tokens/colors';

export interface NavigationButtonProps {
  /**
   * Icon name to display
   */
  icon?: IconName;

  /**
   * Button text label
   */
  text?: string;

  /**
   * Whether to show the icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Whether to show the text
   * @default true
   */
  showText?: boolean;

  /**
   * Visual mode
   * @default 'light'
   */
  mode?: 'light' | 'dark';

  /**
   * Whether the button is in active state
   * @default false
   */
  active?: boolean;

  /**
   * Callback when button is pressed
   */
  onPress?: (e: GestureResponderEvent) => void;
}

export const NavigationButton: React.FC<NavigationButtonProps> = ({
  icon = 'Activity',
  text = 'Activity',
  showIcon = true,
  showText = true,
  mode = 'light',
  active = false,
  onPress,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Determine current state for styling
  const currentState = active ? 'active' : isPressed ? 'active' : isHovered ? 'hover' : 'default';

  // Get styles based on mode and state using Crop Circle color tokens
  const getBackgroundColor = () => {
    if (active) {
      return colors.brand.white; // White background when active
    } else {
      return colors.slate[700]; // Dark background when inactive
    }
  };

  const getTextColor = () => {
    if (active) {
      return colors.brand.black; // Black text when active (on white background)
    } else {
      return colors.brand.white; // White text when inactive (on dark background)
    }
  };

  const backgroundColor = getBackgroundColor();
  const textColor = getTextColor();

  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        styles.container,
        { backgroundColor },
      ]}
    >
      <View style={styles.content}>
        {showIcon && icon && (
          <Icon name={icon} size={24} color={textColor} />
        )}
        {showText && text && (
          <Text style={[styles.text, { color: textColor }]}>
            {text}
          </Text>
        )}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 40,
    borderRadius: 20,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    minWidth: 90,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  text: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 16,
  },
});

NavigationButton.displayName = 'NavigationButton';
